import React from 'react';
import ReactPlayer from 'react-player';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import imageUrlBuilder from '@sanity/image-url';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { PlayButton } from '@tmap/mmm-style-guide/src/Icon';
import PreloadablePicture from '../../preloadablePicture';
import client from '../../../client';

const builder = imageUrlBuilder(client);

const PrimaryPic = styled(PreloadablePicture)(() => ({
  width: '100%',
  minHeight: '100%',
  height: 'auto',
  display: 'contents',
}));

const PrimaryImg = styled('img')(() => ({
  width: '100%',
  minHeight: '100%',
  height: 'auto',
  display: 'block',
}));

const MediaBox = styled(Box)(({ theme }) => ({
  '& .react-player__preview': {
    cursor: 'zoom-in !important',
  },
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  '&': theme.responsiveProperty('borderRadius', { xs: '8px', lg: '12px' }),
}));

const ImagesCounter = styled('div')(({ theme }) => ({
  position: 'absolute',
  padding: `${theme.spacing(0.25)} ${theme.spacing(1)}`,
  background: `${theme.palette.common.black}d`,
  color: theme.palette.common.white,
  borderRadius: '4px',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    fontSize: '80%',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
  },
}));

const ImageOverlayWrapper = styled(Box)(() => ({
  position: 'relative',
}));

const PlayIcon = styled(PlayButton)(({ theme }) => ({
  fontSize: '96px',
  position: 'absolute',
  [theme.breakpoints.down('md')]: {
    fontSize: '64px',
  },
}));

function SingleAspectImage(props) {
  const {
    media,
    totalImageCount,
    aspectRatio = 9 / 16,
  } = props;
  const [preview, alt] = media._type === 'videoBlock' ? [media.previewImage, media.caption] : [media, media.alt];
  const toHeight = (width) => Math.round(width * aspectRatio);

  const primaryPic = preview && (
    <ImageOverlayWrapper>
      {totalImageCount > 1
        && <ImagesCounter>1&ensp;/&ensp;{totalImageCount}</ImagesCounter>}
      <PrimaryPic preload>
        <source
          srcSet={builder.image(preview).format('webp').width(1440).height(toHeight(1440))
            .url()}
          media='(min-width: 750px)'
        />
        <source
          srcSet={builder.image(preview).format('webp').width(800).height(toHeight(800))
            .url()}
          width={800}
          height={toHeight(800)}
          media='(min-width: 600px) and (max-width: 749px)'
        />
        <source
          srcSet={builder.image(preview).format('webp').width(375).height(toHeight(375))
            .url()}
          width={375}
          height={toHeight(375)}
          media='(max-width: 599px)'
        />
        <PrimaryImg
          src={builder.image(preview).width(1440).height(toHeight(1440)).url()}
          width={1440}
          height={toHeight(1440)}
          alt={alt}
        />
      </PrimaryPic>
    </ImageOverlayWrapper>
  );

  const videoEmbed = media._type === 'videoBlock' && (
    <>
      <Box
        sx={{
          position: 'relative',
          paddingTop: `${toHeight(100)}%`,
        }}
      >
        <ReactPlayer
          src={media.url}
          controls
          light={primaryPic || true}
          width='100%'
          height='100%'
          playIcon={<PlayIcon />}
          wrapper='div'
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
      </Box>
      {totalImageCount > 1 && !primaryPic && (
        <ImagesCounter>
          1&ensp;/&ensp;{totalImageCount}
        </ImagesCounter>
      )}
    </>
  );

  return (
    <Grid item xs={12}>
      <MediaBox className='gallery-border-radius'>
        {videoEmbed || primaryPic}
      </MediaBox>
    </Grid>
  );
}

export default SingleAspectImage;
